import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { requestExecutiveApproval, getEligibleExecutives } from 'services/vendor-assessment-service';
import { useAuthTenant } from 'store/auth';
import { Dialog, Autocomplete, TextField } from 'Components/ui';
import { Spinner } from 'Components/common';
import {
  Container,
  ScoreCircleWrap,
  ScoreCircle,
  ScoreCircleText,
  RiskAlert,
  RiskText,
  SectionTitle,
  DeficiencyList,
  DeficiencyItem,
  DeficiencyPrimary,
  DeficiencySecondary,
  FormLabel,
  HelperText,
  SubmitBtn,
} from './ExecutiveApprovalModal.styles';

const str = (val) => {
  if (!val) return ''
  if (typeof val === 'object') return val.en || val.pt || val.es || Object.values(val)[0] || ''
  return String(val)
}

export const ExecutiveApprovalModal = ({ assessmentId, trustScore, onSuccess, onClose }) => {
  const { t } = useTranslation(['gap-analysis']);
  const queryClient = useQueryClient();
  const tenant = useSelector(useAuthTenant);

  const [selectedExecutive, setSelectedExecutive] = useState(null);
  const [justification, setJustification] = useState('');

  const scoreColor = trustScore
    ? (trustScore.percentage >= 90 ? 'var(--vo-success)' : trustScore.percentage >= 70 ? 'var(--vo-warning)' : 'var(--vo-danger)')
    : 'var(--vo-danger)';

  const { data: usersData, isLoading: usersLoading } = useQuery(
    ['executives', tenant?.slug],
    // The tenant's own active users — exactly who the approval request accepts (vo-api-vendor
    // /eligible-executives shares its filter with request validation).
    () => getEligibleExecutives(tenant?.slug),
    { enabled: !!tenant?.slug }
  );

  const executives = usersData?.data?.data || [];

  const requestMutation = useMutation(
    async () => {
      const response = await requestExecutiveApproval(assessmentId, selectedExecutive._id, justification);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to request executive approval');
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', assessmentId]);
        Dialog.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.executiveApproval.swal.successTitle'),
          text: t('gap-analysis:assessment.executiveApproval.swal.successText', { name: selectedExecutive.name }),
          timer: 3000
        });
        setSelectedExecutive(null);
        setJustification('');
        if (onSuccess) onSuccess();
      },
      onError: (error) => {
        if (onClose) onClose();
        Dialog.alert({
          icon: 'error',
          title: t('gap-analysis:assessment.executiveApproval.swal.errorTitle'),
          message: error.message || error.response?.data?.error || t('gap-analysis:assessment.executiveApproval.swal.errorTitle')
        });
      }
    }
  );

  const handleSubmit = () => {
    if (!selectedExecutive) {
      Dialog.alert({
        icon: 'warning',
        title: t('gap-analysis:assessment.executiveApproval.swal.noExecutiveTitle'),
        message: t('gap-analysis:assessment.executiveApproval.swal.noExecutiveText')
      });
      return;
    }

    if (!justification.trim()) {
      Dialog.alert({
        icon: 'warning',
        title: t('gap-analysis:assessment.executiveApproval.swal.noJustificationTitle'),
        message: t('gap-analysis:assessment.executiveApproval.swal.noJustificationText')
      });
      return;
    }

    requestMutation.mutate();
  };

  return (
    <Container>
      {trustScore && (
        <ScoreCircleWrap>
          <ScoreCircle $color={scoreColor}>
            <ScoreCircleText $color={scoreColor}>{trustScore.percentage}{'%'}</ScoreCircleText>
          </ScoreCircle>
        </ScoreCircleWrap>
      )}

      <RiskAlert>
        <RiskText>
          {t('gap-analysis:assessment.executiveApproval.riskWarning')}
        </RiskText>
      </RiskAlert>

      {trustScore?.criticalDeficiencies && trustScore.criticalDeficiencies.length > 0 && (
        <>
          <SectionTitle>
            {t('gap-analysis:assessment.executiveApproval.criticalDeficiencies', { count: trustScore.criticalDeficiencies.length })}
          </SectionTitle>
          <DeficiencyList>
            {trustScore.criticalDeficiencies.slice(0, 3).map((deficiency, idx) => (
              <DeficiencyItem key={idx}>
                <DeficiencyPrimary>{str(deficiency.controlDescription)}</DeficiencyPrimary>
                <DeficiencySecondary>{t('gap-analysis:assessment.executiveApproval.gapLabel', { count: deficiency.gap })}</DeficiencySecondary>
              </DeficiencyItem>
            ))}
            {trustScore.criticalDeficiencies.length > 3 && (
              <DeficiencyItem>
                <DeficiencySecondary>{t('gap-analysis:assessment.executiveApproval.moreDeficiencies', { count: trustScore.criticalDeficiencies.length - 3 })}</DeficiencySecondary>
              </DeficiencyItem>
            )}
          </DeficiencyList>
        </>
      )}

      <FormLabel>{t('gap-analysis:assessment.executiveApproval.selectExecutive')} *</FormLabel>
      <Autocomplete
        options={executives}
        getOptionLabel={(exec) => `${exec.name} (${exec.email})`}
        value={selectedExecutive}
        onChange={(value) => setSelectedExecutive(value)}
        placeholder={t('gap-analysis:assessment.executiveApproval.selectExecutivePlaceholder')}
        loading={usersLoading}
        disabled={usersLoading}
      />
      <HelperText>{t('gap-analysis:assessment.executiveApproval.selectExecutiveHelper')}</HelperText>

      <FormLabel>{t('gap-analysis:assessment.executiveApproval.justification')} *</FormLabel>
      <TextField
        required
        multiline
        fullWidth
        rows={4}
        value={justification}
        onChange={(e) => setJustification(e.target.value)}
        placeholder={t('gap-analysis:assessment.executiveApproval.justificationPlaceholder')}
      />
      <HelperText>{t('gap-analysis:assessment.executiveApproval.justificationHelper')}</HelperText>

      <SubmitBtn
        onClick={handleSubmit}
        disabled={requestMutation.isLoading || !selectedExecutive || !justification.trim()}
      >
        {requestMutation.isLoading ? <Spinner $size={24} /> : t('gap-analysis:assessment.executiveApproval.sendButton')}
      </SubmitBtn>
    </Container>
  );
};
